import { useEffect, useState } from "react";

import {
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";

import { X } from "@phosphor-icons/react";

import useLesson from "@/contexts/useLesson";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { Problem, QuestionType } from "@/types";

import EditProblem from "./EditProblem";
import ViewProblem from "./ViewProblem";

interface Props {
  problem: Problem;
  index: number;
  open: boolean;
  onClose: () => void;
  canSubmit?: boolean;
  onApprove?: () => void;
  onEdit?: () => void;
  onRegenerate?: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
  onUpdate?: (index: number, problem: Problem) => void;
  onCancelEdit?: () => void;
}

const ProblemModal = ({
  problem,
  index,
  open,
  onClose,
  canSubmit = true,
  onApprove,
  onEdit,
  onRegenerate,
  onDelete,
  isEditing = false,
  onUpdate,
  onCancelEdit,
}: Props) => {
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const { lesson } = useLesson();
  const { aiAxios } = useFirebaseContext();

  const handleSubmit = () => {
    setLoading(true);
    aiAxios
      .post("luna/problems/submit", {
        lessonId: lesson?.id,
        problemId: problem.id,
        studentAnswer: answer,
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (problem?.studentAnswer) {
      setAnswer(problem?.studentAnswer);
    } else {
      setAnswer("");
    }
  }, [problem?.studentAnswer, open]);

  const handleClose = () => {
    onClose();
  };

  const isAnswerValid = () => {
    if (!answer.trim()) return false;
    if (problem?.type === QuestionType.TrueFalse) {
      return answer === "true" || answer === "false";
    }
    return true;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="text14B">{problem.title || `Problem ${index + 1}`}</Typography>
        <IconButton
          aria-label="Close problem dialog"
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: -24,
            right: -24,
            color: "common.white",
          }}
        >
          <X size={18} weight="bold" />
        </IconButton>
      </DialogTitle>
      {isEditing ? (
        <EditProblem
          problem={problem}
          index={index}
          onUpdate={onUpdate || (() => { })}
          onCancelEdit={onCancelEdit || (() => { })}
        />
      ) : (
        <ViewProblem
          problem={problem}
          answer={answer}
          setAnswer={setAnswer}
          canSubmit={canSubmit}
          loading={loading}
          handleSubmit={handleSubmit}
          isAnswerValid={isAnswerValid}
          onApprove={onApprove}
          onEdit={onEdit}
          onRegenerate={onRegenerate}
          onDelete={onDelete}
        />
      )}
    </Dialog>
  );
};

export default ProblemModal;
